import React, {useCallback, useEffect, useRef} from 'react';
import {useAudioPlayer} from 'react-use-audio-player';
import {Box, Stack, styled} from '@mui/material';
import {Voice} from '@contentlab/api/avatars';
import {contentLabActions} from '@contentlab/stores/contentLabStore';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import EmptyFilterationResults from '@contentlab/views/contentlab/pages/ai-avatar-page/components/ai-avatar-editor/subcomponents/EmptyFilterationResults';
import translate from '@/utils/translate';
import VoiceCard from './VoiceCard';

const VOICE_CARD_GAP = 12;
const VOICE_SCROLL_TOP_KEY = 'voiceScrollTop';

const Container = styled(Stack)({
  overflowY: 'auto',
  paddingBottom: '20px',
  paddingInlineEnd: '4px',
  height: '100%',
  ...scrollbarMixins.overrideScrollbarHover(),
});

const OptionsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: `${VOICE_CARD_GAP}px`,
});

function VoiceOnlyEditor({
  selectedId = null,
  onSelectVoice,
  voices,
  isLoading,
}: {
  selectedId?: string | null;
  onSelectVoice?: (voice: Voice) => void;
  voices: Voice[];
  isLoading?: boolean;
}) {
  const playingIdRef = useRef<string | null>(null);
  const {load, stop, isPlaying} = useAudioPlayer();

  const formatUseCase = (useCase: string) => {
    return useCase
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const voiceOptions = (voices || []).map((v) => ({
    label: v.name,
    description: `${v.gender}, ${v.age}, ${formatUseCase(v.useCase)}, ${formatUseCase(v.accent)}`,
    id: v.id,
    sample: v.voicePreview,
  }));

  // Controlled selection from parent; null means no selection
  const effectiveSelectedId = selectedId ?? '';

  // For single column, we can use a simpler approach without virtualization
  // since voice cards are typically not in huge quantities
  const parentRef = useRef<HTMLDivElement>(null);

  // Handle scroll position persistence
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if (event.target) {
      const {scrollTop} = event.target as HTMLDivElement;
      sessionStorage.setItem(VOICE_SCROLL_TOP_KEY, scrollTop.toString());
    }
  }, []);

  // Restore scroll position on mount
  useEffect(() => {
    const savedScrollTop = sessionStorage.getItem(VOICE_SCROLL_TOP_KEY);
    if (savedScrollTop && parentRef.current) {
      parentRef.current.scrollTop = Number(savedScrollTop);
    }
  }, []);

  if (!isLoading && voices.length === 0) {
    return (
      <EmptyFilterationResults
        title={translate('No exact matches')}
        subtitle={translate('Adjust your filters or browse the full library')}
        ctaText={translate('Clear All Filters')}
        onCtaClick={() => contentLabActions.resetSelectedAvatarFilters()}
      />
    );
  }

  return (
    <Container ref={parentRef} onScroll={handleScroll}>
      <OptionsContainer>
        {voiceOptions.map((opt) => {
          const voice = voices.find((v) => String(v.id) === String(opt.id));
          return (
            <Box key={opt.id}>
              <VoiceCard
                id={opt.id}
                name={opt.label}
                description={opt.description}
                isSelected={String(effectiveSelectedId) === String(opt.id)}
                isPlaying={playingIdRef.current === String(opt.id) && isPlaying}
                onSelect={() =>
                  onSelectVoice?.(
                    voices.find((v) => String(v.id) === String(opt.id))!
                  )
                }
                onPreview={(id) => {
                  const selected = voiceOptions.find((v) => v.id === id);
                  if (selected?.sample) {
                    const isSame = playingIdRef.current === String(id);
                    if (isSame && isPlaying) {
                      stop();
                      playingIdRef.current = null;
                    } else {
                      stop();
                      load(selected.sample, {
                        autoplay: true,
                        onend: () => {
                          playingIdRef.current = null;
                        },
                      });
                      playingIdRef.current = String(id);
                    }
                  }
                }}
              />
            </Box>
          );
        })}
      </OptionsContainer>
    </Container>
  );
}

export default VoiceOnlyEditor;
